import { type z } from 'zod';

/**
 * Assert that the input of a schema is the same as the type `T`.
 *
 * This is useful if your schema takes some API data, does transformations
 * on it, and we want to ensure that the schema's input remains in sync with
 * any API changes.
 *
 * @example
 * ```typescript
 * import { type AppResponse } from '@autone/utils';
 *
 * // Define your API response type using AppResponse generic
 * type GetProductDataResponse = AppResponse<
 *   'app',
 *   'post',
 *   '/app/products/{product_id}/data',
 *   200
 * >;
 *
 * // Create a schema that transforms the API response - this will not compile
 * // if the input does not match the API response type
 * const productDataSchema = assertSchemaInput<GetProductDataResponse>()(
 *   z.object({
 *     name: z.string(),
 *     price: z.number(),
 *   }).transform(({ name, price }) => ({
 *     name: name.toUpperCase(),
 *     price,
 *     displayPrice: `$${price}`,
 *   }))
 * );
 * ```
 */
export const assertSchemaInput =
  <T>() =>
  <S extends z.ZodSchema<unknown, z.ZodTypeDef, T>>(schema: S) =>
    schema;

/**
 * Assert that the output of a schema is the same as the type `T`.
 *
 * This is useful when you want to ensure the transformed output of a schema
 * matches a specific type, particularly after using `.transform()`.
 *
 * @example
 * ```typescript
 * type TransformedProduct = {
 *   name: string;
 *   displayPrice: string;
 * };
 *
 * // This will not compile if the transform output doesn't match TransformedProduct
 * const productSchema = assertSchemaOutput<TransformedProduct>()(
 *   z.object({
 *     name: z.string(),
 *     price: z.number(),
 *   }).transform(({ name, price }) => ({
 *     name,
 *     displayPrice: `$${price}`,
 *   }))
 * );
 * ```
 */
export const assertSchemaOutput =
  <T>() =>
  <S extends z.ZodSchema<T, z.ZodTypeDef, unknown>>(schema: S) =>
    schema;

/**
 * Assert that both the input and output of a schema match the specified types.
 *
 * This is useful when you want to ensure that a schema's input matches an API
 * response type AND the transformed output matches a specific application type.
 *
 * @example
 * ```typescript
 * import { type AppResponse } from '@autone/utils';
 *
 * // Define your API response type
 * type GetProductDataResponse = AppResponse<
 *   'app',
 *   'post',
 *   '/app/products/{product_id}/data',
 *   200
 * >;
 *
 * // Define your transformed output type
 * type TransformedProduct = {
 *   name: string;
 *   displayPrice: string;
 * };
 *
 * // This will not compile if input doesn't match GetProductDataResponse
 * // OR if output doesn't match TransformedProduct
 * const productSchema = assertSchema<GetProductDataResponse, TransformedProduct>()(
 *   z.object({
 *     name: z.string(),
 *     price: z.number(),
 *   }).transform(({ name, price }) => ({
 *     name,
 *     displayPrice: `$${price}`,
 *   }))
 * );
 * ```
 */
export const assertSchema =
  <TInput, TOutput>() =>
  <S extends z.ZodSchema<TOutput, z.ZodTypeDef, TInput>>(schema: S) =>
    schema;
